import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Typography,
    CircularProgress,
    Button,
    Alert
} from '@mui/material';
import Cookies from 'js-cookie';
import { formatCredits } from '../../../utils/numberFormat';
import FlutterServicesAPI from '../../../services/flutterServices';
import { aiGenerationAPI } from '../../../services/merchantAPI';
import { API_URLS } from '../../../config/apiConfig';
import { getSecureVideoStreamUrl, getThumbnailUrl as getSecureThumbnailUrl } from '../../../utils/imageUrlUtils';
import SecureImage from '../../Common/SecureImage';
import SecureVideo from '../../Common/SecureVideo';
import ShareSocialButton from '../../Common/ShareSocialButton';
import PinchZoomPanContainer from '../../Common/PinchZoomPanContainer';
import {
    getGenerationDownloadBaseName,
    getGenerationDownloadFileName
} from '../../../utils/generationDownloadFilename';
import { PlayArrow } from '@mui/icons-material';


const GenerationPreviewDialog = ({
    open,
    onClose,
    generation,
    onDownloadSuccess,
}) => {
    const [modalImageLoading, setModalImageLoading] = useState(true);
    const [modalImageError, setModalImageError] = useState(false);
    const [zoomDialog, setZoomDialog] = useState(false);
    const [zoomedImage, setZoomedImage] = useState(null);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [downloading, setDownloading] = useState(false);

    const isImageLoadedRef = useRef(false);

    useEffect(() => {
        if (open && generation) {
            isImageLoadedRef.current = false;
            // setModalImageLoading(true);
            // Skip loading state for static demo data
            setModalImageLoading(generation.id !== 'SAMPLE');
            setModalImageError(false);
            setError('');
            setSuccessMessage('');
        }
    }, [open, generation]);

    if (!generation) return null;

    const getFileExtension = (outputFormat) => {
        if (!outputFormat || !outputFormat.name) return 'jpg';
        const name = outputFormat.name.toUpperCase();
        if (name.includes('VIDEO')) return 'mp4';
        if (name.includes('GIF')) return 'gif';
        return 'jpg';
    };

    const isReel = generation.jewelry_category?.display_name === 'Reel';
    const isVideo = generation.output_format?.name === 'VIDEO' || isReel || generation.id === 'SAMPLE';

    // Same rule as Gallery getThumbnailUrl: use enhanced still for video/reel when the API stored one.
    const getVideoThumbnailUrl = () => {
        if (generation.id === 'SAMPLE') return '/assets/img/sample-preview.png';
        if ((generation.output_format?.name === 'VIDEO' || isReel) && generation.enhanced_image_path) {
            return getSecureThumbnailUrl(generation.id, '1200x1200', 'enhanced');
        }
        return getSecureThumbnailUrl(generation.id, '1200x1200');
    };

    const handleZoomImage = () => {
        setZoomedImage({
            src: generation.id === 'SAMPLE'
                ? '/assets/video/glammify_395.mp4'
                : (isVideo ? getSecureVideoStreamUrl(generation.id) : getSecureThumbnailUrl(generation.id, '1200x1200')),
            type: isVideo ? 'video' : 'image',
            id: generation.id,
        });
        setZoomDialog(true);
    };

    const formatCreatedDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        // 12-hour clock with am/pm (matches History list formatting intent).
        let h24 = d.getHours();
        const ampm = h24 >= 12 ? 'pm' : 'am';
        let h12 = h24 % 12;
        if (h12 === 0) h12 = 12;
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year}, ${h12}:${minutes} ${ampm}`;
    };

    const handleDownload = async () => {
        try {
            setDownloading(true);
            setError('');

            if (FlutterServicesAPI.isFlutterApp()) {
                const accessToken = Cookies.get('merchant_access_token');
                if (!accessToken) {
                    setError('Authentication required for download');
                    return;
                }

                const downloadUrl = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/download/${generation.id}`;
                const downloadData = {
                    generationId: generation.id,
                    fileType: isVideo ? 'VIDEO' : (generation.output_format?.name || 'IMAGE'),
                    fileName: getGenerationDownloadBaseName(generation),
                    downloadUrl: downloadUrl,
                    fileExtension: isVideo ? 'mp4' : getFileExtension(generation.output_format),
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    accessToken: accessToken
                };

                const mobileResult = await FlutterServicesAPI.downloadFileToStorage(downloadData);
                if (mobileResult && mobileResult.success) {
                    // setSuccessMessage('File downloaded to device storage successfully!');
                    if (onDownloadSuccess) onDownloadSuccess(generation);
                } else {
                    setError(mobileResult?.message || 'Mobile download failed');
                }
            } else {
                const response = await aiGenerationAPI.downloadGeneration(generation.id);
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const extension = isVideo ? 'mp4' : getFileExtension(generation.output_format);
                link.setAttribute(
                    'download',
                    getGenerationDownloadFileName(generation, extension)
                );
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                    link.remove();
                    window.URL.revokeObjectURL(url);
                }, 5000);
                setSuccessMessage('File downloaded successfully!');
                if (onDownloadSuccess) onDownloadSuccess(generation);
            }
        } catch (err) {
            setError('Failed to download file. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    // Build dynamic details
    const detailsItems = [
        {
            label: 'Category',
            value: generation.jewelry_category?.display_name || 'N/A',
        },
        {
            label: FlutterServicesAPI.getCreditLabel(),
            value: generation.credits_cost ? formatCredits(generation.credits_cost) : '0',
        },
        {
            label: 'Ethnicity',
            value: generation.ethnicity?.display_name || generation.model_ethnicity?.display_name || 'N/A',
        },
        {
            label: 'Created',
            value: formatCreatedDate(generation.created_at),
        },
        {
            label: 'Clothing Type',
            value: generation.clothing_type?.display_name || generation.model_clothing?.display_name || 'N/A',
            fullWidth: true,
        },
        {
            label: 'Caption 1',
            value: generation.caption_text_1 || 'N/A',
            fullWidth: true,
        },
        {
            label: 'Caption 2',
            value: generation.caption_text_2 || 'N/A',
            fullWidth: true,
        },
    ];

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xs"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '17.39px',
                        width: { xs: '92vw', sm: '500px' },
                        maxWidth: '960px',
                        minHeight: { sm: '480px' },
                        maxHeight: '95vh',
                        margin: { xs: '10px', sm: 'auto' },
                        overflow: 'hidden',
                    }
                }}
            >
                {/* Header */}
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #E5E7EB',
                        px: 2.5,
                        py: 1,
                        pb: 1.5,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '17.39px',
                            fontFamily: 'var(--font-montserrat-semibold)',
                            fontWeight: 600,
                            color: '#000000',
                            lineHeight: 1.3,
                        }}
                    >
                        Your Creation
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            bgcolor: '#651BC6',
                            color: 'white',
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                                bgcolor: '#5115a3',
                            },
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M11.7376 3.91248L3.9126 11.7375" stroke="white" strokeWidth="1.63021" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M3.9126 3.91248L11.7376 11.7375" stroke="white" strokeWidth="1.63021" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </IconButton>
                </DialogTitle>

                <DialogContent className="preview-content" sx={{ p: { xs: 2, sm: 2.5 } }}>
                    {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                    {successMessage && <Alert severity="success" sx={{ mb: 1.5 }}>{successMessage}</Alert>}

                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 2, sm: 2 },
                        paddingTop: { sm: 1 },
                        alignItems: { xs: 'stretch', sm: 'flex-start' }
                    }}>
                        {/* Image Section - Left on desktop */}
                        <Box
                            sx={{
                                width: { xs: '100%', sm: '190px' },
                                position: 'relative',
                                borderRadius: '9.16px',
                                overflow: 'hidden',
                                boxShadow: '0px 1.83px 9.16px 0px rgba(0,0,0,0.07)',
                                backgroundColor: '#f5f5f5',
                                flexShrink: 0,
                                // aspectRatio: '3/5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {/* Video: show grid-style thumbnail immediately while the stream buffers (image path uses the same thumb, so no extra layer). */}


                            {/* Loading State — solid backdrop for images; light veil over video thumbnail so the still stays visible */}


                            {/* Error State */}
                            {modalImageError && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: '#f5f5f5',
                                        zIndex: 1,
                                    }}
                                >
                                    <Typography variant="body2" color="error" sx={{ fontSize: 13 }}>
                                        Failed to load preview
                                    </Typography>
                                </Box>
                            )}

                            {/* Actual Image / Video Preview */}
                            {isVideo ? (
                                <SecureVideo
                                    src={generation.id === 'SAMPLE' ? '/assets/video/glammify_395.mp4' : getSecureVideoStreamUrl(generation.id)}
                                    poster={generation.id === 'SAMPLE' ? '/assets/img/sample-preview.png' : getVideoThumbnailUrl()}
                                    autoPlay={false}
                                    muted={true}
                                    loop={false}
                                    controls={false}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        // objectFit: 'contain',
                                        display: modalImageError ? 'none' : 'block',
                                        cursor: 'pointer',
                                        objectFit: 'cover',
                                    }}
                                    onClick={handleZoomImage}
                                    // Stream URL is ready before first frame; clear loading so the play overlay matches Gallery.
                                    onLoad={() => {
                                        setModalImageLoading(false);
                                        setModalImageError(false);
                                    }}
                                    onLoadedData={() => {
                                        setModalImageLoading(false);
                                        setModalImageError(false);
                                    }}
                                    onError={() => {
                                        setModalImageLoading(false);
                                        setModalImageError(true);
                                    }}
                                />
                            ) : (
                                <SecureImage
                                    src={generation.id === 'SAMPLE' ? '/assets/img/sample-preview.png' : getSecureThumbnailUrl(generation.id, '1200x1200')}
                                    alt="Your Creation Preview"
                                    useThumbnail={true}
                                    thumbnailSize="1200x1200"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        // objectFit: 'contain',
                                        display: modalImageError ? 'none' : 'block',
                                        cursor: 'pointer',
                                    }}
                                    onClick={handleZoomImage}
                                    onError={() => {
                                        setModalImageLoading(false);
                                        setModalImageError(true);
                                    }}
                                    onLoad={() => {
                                        isImageLoadedRef.current = true;
                                        setModalImageLoading(false);
                                        setModalImageError(false);
                                    }}
                                />
                            )}

                            {/* Play icon on poster/thumbnail immediately (same as Gallery); do not wait for full video decode. */}
                            {isVideo && !modalImageError && (
                                <Box
                                    onClick={handleZoomImage}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        zIndex: 2,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Box sx={{ position: 'absolute', border: '2px solid #6400CD', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3, pointerEvents: 'none' }}>
                                        <PlayArrow sx={{ color: '#6400CD', fontSize: 24 }} />
                                    </Box>
                                </Box>
                            )}

                            {/* Zoom Icon */}
                            {!modalImageLoading && !modalImageError && !isVideo && (
                                <Box
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleZoomImage();
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 8,
                                        left: 12,
                                        zIndex: 3,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '&:hover': {
                                            transform: 'scale(1.1)',
                                        },
                                        transition: 'transform 0.2s ease',
                                    }}
                                >
                                    <img
                                        src="/assets/img/icons/zoom-imge.svg"
                                        alt="Zoom"
                                        style={{ width: 45, height: 45 }}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* Details Section - Right on desktop */}
                        <Box sx={{ flex: 1 }}>
                            {/* Details Grid */}
                            <Box className="generation-details"
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr' },
                                }}
                            >
                                {detailsItems.map((item, idx) => (
                                    <Box className="mb-3"
                                        key={idx}
                                        sx={{
                                            gridColumn: item.fullWidth ? '1 / -1' : 'auto',
                                        }}
                                    >
                                        <Typography className="item-label">
                                            {item.label}
                                        </Typography>

                                        <Typography className="item-value">
                                            {item.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                {/* Buttons */}
                <DialogActions className="dialog-actions-custom" sx={{ px: { xs: 2.5, sm: 2.5 }, pb: 2.5, justifyContent: 'center' }}>
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        width: '100%',
                        maxWidth: { sm: '320px' },
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mx: 'auto'
                    }}>
                        {/* Share Button */}
                        <ShareSocialButton
                            imageUrl={generation.id === 'SAMPLE'
                                ? '/assets/video/glammify_395.mp4'
                                : (isVideo ? getSecureVideoStreamUrl(generation.id) : getSecureThumbnailUrl(generation.id, '1200x1200'))}
                            isVideo={isVideo}
                            size="medium"
                            multiShare={true}
                            selectedItems={[generation.id]}
                            selectedItemsData={[generation]}
                            getPreviewUrl={(item) => item.id === 'SAMPLE' ? '/assets/video/glammify_395.mp4' : (item.output_format?.name === 'VIDEO' || item.jewelry_category?.display_name === 'Reel' ? getSecureVideoStreamUrl(item.id) : getSecureThumbnailUrl(item.id, '1200x1200'))}
                            getFileType={(item) => item.id === 'SAMPLE' ? 'video' : (item.output_format?.name === 'VIDEO' || item.jewelry_category?.display_name === 'Reel' ? 'video' : 'image')}
                            onShareStatus={(status) => {
                                if (status?.type === 'success') setSuccessMessage(status.message);
                                else if (status?.type === 'error') setError(status.message);
                            }}
                            variant="button"
                            className="btn-primary p-12-0 font-14"
                            hideIcon={true}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <img
                                        src="/assets/img/icons/share-icon.svg"
                                        alt="Share"
                                        style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                                    />
                                    Share
                                </Box>
                            }
                            sx={{
                                flex: 1,
                                height: '42px !important',
                                borderRadius: '10px !important',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                justifyContent: 'center',
                                textTransform: 'none'
                            }}
                        />

                        {/* Download Button */}
                        {generation.is_downloadable && (
                            <Button
                                className="btn-primary p-12-0 font-14 ms-0"
                                onClick={handleDownload}
                                disabled={downloading}
                                sx={{
                                    flex: 1,
                                    height: '42px !important',
                                    borderRadius: '10px !important',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    justifyContent: 'center',
                                    textTransform: 'none'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {downloading
                                        ? <CircularProgress size={18} color="inherit" />
                                        : <img
                                            src="/assets/img/icons/download-icon.svg"
                                            alt="Download"
                                            style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                                        />
                                    }
                                    {downloading
                                        ? 'Downloading...'
                                        : 'Download'}
                                </Box>
                            </Button>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Zoom Dialog */}
            <Dialog
                open={zoomDialog}
                onClose={() => { setZoomDialog(false); }}
                maxWidth={false}
                sx={{
                    '& .MuiDialog-paper': {
                        margin: 0,
                        padding: 0,
                        backgroundColor: '#000',
                        boxShadow: 'none',
                        maxWidth: 'none',
                        maxHeight: 'none',
                        width: '100vw',
                        height: '100vh',
                        overflow: 'hidden'
                    }
                }}
            >
                {zoomedImage && (
                    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <PinchZoomPanContainer>
                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                {zoomedImage.type === 'video' ? (
                                    <SecureVideo
                                        src={zoomedImage.src}
                                        poster={generation.id === 'SAMPLE' ? '/assets/img/sample-preview.png' : getVideoThumbnailUrl()}
                                        controls
                                        autoPlay={true}
                                        muted={true}
                                        style={{ maxWidth: '95vw', maxHeight: '95vh', borderRadius: '8px', backgroundColor: '#000' }}
                                    />
                                ) : (
                                    <SecureImage
                                        src={zoomedImage.src}
                                        alt="Zoomed"
                                        useThumbnail={false}
                                        style={{ maxWidth: '95vw', maxHeight: '95vh', borderRadius: '8px', backgroundColor: '#000' }}
                                    />
                                )}
                                {/* Close Button - Attached to top right of media box */}
                                <IconButton
                                    onClick={() => { setZoomDialog(false); }}
                                    sx={{
                                        position: 'absolute',
                                        top: { xs: -12, md: -9 },
                                        right: { xs: -9, md: -32 },
                                        bgcolor: '#651BC6',
                                        color: 'white',
                                        width: { xs: 32, sm: 40 },
                                        height: { xs: 32, sm: 40 },
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '&:hover': { bgcolor: '#5115a3' },
                                        zIndex: 30,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M11.7376 3.91248L3.9126 11.7375" stroke="white" strokeWidth="1.63021" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M3.9126 3.91248L11.7376 11.7375" stroke="white" strokeWidth="1.63021" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </IconButton>
                            </Box>
                        </PinchZoomPanContainer>
                    </Box>
                )}
            </Dialog>
        </>
    );
};

export default GenerationPreviewDialog;
